import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useGetZone } from 'src/features/zone/api/useGetZone';
import ZoneForm from 'src/features/zone/components/form/ZoneForm';
import { zoneFormSchema, ZoneFormSchemaValues } from 'src/features/zone/utils/formSchema';
import { Zone } from 'src/features/zone/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface ZoneFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedZone?: Zone;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: ZoneFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function ZoneFormModal<TData>({
    selectedZone,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: ZoneFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    // We are in edit mode if we have a selected zone
    // otherwise it means we are creating a new zone

    const { data: zone } = useGetZone({
        zoneId: selectedZone?.id ?? '',
        parkingId,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedZone?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            zone
                ? {
                      ...zone,
                      site: zone.site.id,
                      service_provider: zone.service_provider.id,
                      parent_zone: zone.parent_zone?.id || '',
                      user_access: zone.user_access.map((access) => access.id),
                  }
                : {
                      translations: [],
                      visitor_categories: undefined,
                      type: undefined,
                      capacity_limits: [],
                      limit_access_per_capacity: undefined,
                      limit_user_access: undefined,
                      site: '',
                      service_provider: '',
                      parent_zone: '',
                      user_access: [],
                  },
        [zone],
    );

    const form = useForm<ZoneFormSchemaValues>({
        resolver: zodResolver(zoneFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        if (isModalOpen) {
            form.reset(defaultValues);
        }
    }, [isModalOpen, defaultValues, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: ZoneFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, form, onError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ZoneForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedZone && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ZoneFormModal;
